import { FC } from 'react';
import styled from 'styled-components';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFormContext, useWatch } from 'react-hook-form';

import { useGetAssetThumbnailUrlQuery } from '../../../../services/assets.service';
import { usePlacematContext } from './PlacematContext';

interface ShapeVariable {
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  optional: boolean;
}

interface XLayout {
  templateSize: { width: number; height: number };
  shapeVariables: ShapeVariable[];
}

interface PlacematLayoutPreviewProps {
  xLayout: XLayout;
  thumbnailUrl?: string | null;
  activeSlot: string | null;
  onSlotClick?: (slotName: string) => void;
  placeholderMessage?: string;
}

const StyledPreviewContainer = styled.div<{ $hasThumbnail: boolean }>`
  position: relative;
  width: 100%;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.palette.stroke};
  border-radius: ${({ theme }) => theme?.radius?.small};
  background-color: ${({ $hasThumbnail }) => ($hasThumbnail ? 'transparent' : '#f3f4f6')};
  background-size: 100% 100%;
`;

const StyledAspectBox = styled.div<{ $ratio: number }>`
  position: relative;
  width: 100%;
  padding-top: ${({ $ratio }) => $ratio * 100}%;
`;

const StyledSlotZone = styled.div<{ $active: boolean; $filled: boolean; $disabled: boolean }>`
  position: absolute;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-width: 2px;
  border-style: ${({ $active, $filled }) => ($active || $filled ? 'solid' : 'dashed')};
  border-color: ${({ $active, $disabled }) => ($disabled ? '#e5e7eb' : $active ? '#0A6EFF' : '#d1d5db')};
  background-color: ${({ $disabled }) => ($disabled ? '#f9fafb' : '#f3f4f6')};
  transition: border-color 0.15s, box-shadow 0.15s;

  ${({ $disabled }) =>
    !$disabled &&
    `
    &:hover {
      border-color: #0A6EFF;
      border-style: solid;
    }
  `}
`;

const StyledSlotContent = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  pointer-events: none;
  color: ${({ $active }) => ($active ? '#0A6EFF' : '#9ca3af')};
`;

const StyledSlotText = styled.span`
  font-size: 9px;
  font-weight: 600;
`;

const StyledSlotThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const StyledEmptyBackground = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 11px;
`;

const StyledPlaceholderMessage = styled(StyledEmptyBackground)`
  font-size: ${({ theme }) => theme.fontSize.body};
  text-align: center;
  padding: 12px;
`;

interface SlotZoneProps {
  shape: ShapeVariable;
  isActive: boolean;
  style: React.CSSProperties;
  onClick: () => void;
  disabled: boolean;
}

const SlotZone: FC<SlotZoneProps> = ({ shape, isActive, style, onClick, disabled }) => {
  const { slotFieldName } = usePlacematContext();
  const { control } = useFormContext();

  const items = useWatch({ control, name: slotFieldName as string });
  const assetId = slotFieldName ? items?.[shape.name] : undefined;

  const { currentData: thumbnail } = useGetAssetThumbnailUrlQuery(assetId ? { assetId: Number(assetId) } : skipToken);

  return (
    <StyledSlotZone
      $active={isActive}
      $filled={!!assetId}
      $disabled={disabled}
      style={style}
      onClick={disabled ? undefined : onClick}
      title={shape.label}
    >
      {thumbnail ? (
        <StyledSlotThumbnail src={thumbnail} alt={shape.label} />
      ) : (
        <StyledSlotContent $active={isActive}>
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 15l-5-5L5 21" />
          </svg>
          <StyledSlotText>Click to fill</StyledSlotText>
        </StyledSlotContent>
      )}
    </StyledSlotZone>
  );
};

export const PlacematLayoutPreview: FC<PlacematLayoutPreviewProps> = ({
  xLayout,
  thumbnailUrl,
  activeSlot,
  onSlotClick,
  placeholderMessage,
}) => {
  const { templateSize, shapeVariables } = xLayout;
  const ratio = templateSize.height / templateSize.width;
  const hasShapes = shapeVariables.length > 0;
  const disabled = !onSlotClick;

  return (
    <StyledPreviewContainer
      $hasThumbnail={!!thumbnailUrl && !placeholderMessage}
      style={thumbnailUrl && !placeholderMessage ? { backgroundImage: `url(${thumbnailUrl})` } : undefined}
    >
      <StyledAspectBox $ratio={ratio}>
        {placeholderMessage && <StyledPlaceholderMessage>{placeholderMessage}</StyledPlaceholderMessage>}
        {!placeholderMessage && !thumbnailUrl && !hasShapes && (
          <StyledEmptyBackground>No layout configured</StyledEmptyBackground>
        )}

        {!placeholderMessage &&
          shapeVariables.map((shape) => {
            const left = (shape.x / templateSize.width) * 100;
            const top = (shape.y / templateSize.height) * 100;
            const width = (shape.width / templateSize.width) * 100;
            const height = (shape.height / templateSize.height) * 100;

            return (
              <SlotZone
                key={shape.name}
                shape={shape}
                isActive={!disabled && activeSlot === shape.name}
                disabled={disabled}
                style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}
                onClick={() => onSlotClick?.(shape.name)}
              />
            );
          })}
      </StyledAspectBox>
    </StyledPreviewContainer>
  );
};
